/**
 * usePositionManagement Hook
 * 
 * Hook for managing position add/remove/copy operations.
 * Extracted from StrategyWizardDialog.tsx for better maintainability.
 */

import { useCallback } from 'react';
import { Leg, PositionActions } from '../../types/strategy';
import {
  createNewPosition,
  clonePosition,
  updatePositionInArray,
  removePositionFromArray,
  autoCorrectPositionInterdependencies,
  mainRecycleEligible
} from '../../utils/strategy';

interface UsePositionManagementProps {
  legs: Leg[];
  setLegs: React.Dispatch<React.SetStateAction<Leg[]>>;
  showError: (message: string) => void;
}

interface UsePositionManagementReturn {
  actions: PositionActions;
  updatePosition: (legId: string, updates: Partial<Leg>) => void;
}

export const usePositionManagement = ({
  legs,
  setLegs,
  showError
}: UsePositionManagementProps): UsePositionManagementReturn => {

  // Add new position
  const addPosition = useCallback(() => {
    const newPosition = createNewPosition();
    setLegs(prev => [...prev, newPosition]);
  }, [setLegs]);

  // Add a protective hedge for a main leg. A hedge only makes sense on a SELL
  // main (it caps the short's tail risk), so it is BUY-only, inherits the main's
  // option type AND lot count, carries no risk features, and is inserted right
  // after its main so the builder renders it grouped underneath. No-ops if the
  // target is missing, is itself a hedge, is NOT a SELL leg, or already has a
  // hedge (one per main).
  const addHedge = useCallback((mainLegId: string) => {
    setLegs(prev => {
      const mainIdx = prev.findIndex(leg => leg.id === mainLegId);
      if (mainIdx < 0) return prev;
      const main = prev[mainIdx];
      if (main.isHedge || main.actionType !== 'SELL') return prev;
      if (prev.some(leg => leg.isHedge && leg.hedgeForLegId === mainLegId)) return prev;
      const hedge: Leg = {
        ...createNewPosition(),
        optionType: main.optionType,
        actionType: 'BUY',
        totalLots: main.totalLots,
        // Default a hedge to PREMIUM strike selection, CLOSEST operator, value 5
        // — a protective OTM long is naturally chosen by target premium.
        selectionMethod: 'PREMIUM',
        premiumOperator: 'CLOSEST',
        premiumValue: 5,
        isHedge: true,
        hedgeForLegId: mainLegId,
      };
      const next = [...prev];
      next.splice(mainIdx + 1, 0, hedge);
      return next;
    });
  }, [setLegs]);

  // Remove position. Removing a MAIN leg cascades to its dependent hedge (a
  // hedge with no live main is invalid); removing a hedge just removes itself.
  const removePosition = useCallback((legId: string) => {
    setLegs(prev => removePositionFromArray(prev, legId).filter(leg => leg.hedgeForLegId !== legId));
  }, [setLegs]);
  
  // Copy existing position
  const copyPosition = useCallback((legId: string) => {
    const positionToCopy = legs.find(leg => leg.id === legId);
    if (positionToCopy) {
      const newPosition = clonePosition(positionToCopy);
      setLegs(prev => [...prev, newPosition]);
    }
  }, [legs, setLegs]);
  
  // Update specific position
  const updatePosition = useCallback((legId: string, updates: Partial<Leg>) => {
    setLegs(prev => {
      const updatedLegs = updatePositionInArray(prev, legId, updates);
      // Auto-correct interdependencies
      let next = updatedLegs.map(leg =>
        leg.id === legId ? autoCorrectPositionInterdependencies(leg) : leg
      );
      // A hedge mirrors its main leg's option type — cascade a main's option-type
      // change to its hedge so they never diverge (the hedge's toggle is locked in
      // the builder; this keeps the stored value in sync when the main changes).
      if (updates.optionType !== undefined) {
        const main = next.find(l => l.id === legId);
        if (main && !main.isHedge) {
          next = next.map(l =>
            (l.isHedge && l.hedgeForLegId === legId)
              ? { ...l, optionType: main.optionType }
              : l
          );
        }
      }
      // A hedge mirrors its main leg's LOTS (equal-lots-only) — cascade a main's lot
      // change to its hedge so they never diverge (the hedge's lot field is locked in
      // the builder; this keeps the stored value in sync when the main changes).
      if (updates.totalLots !== undefined) {
        const main = next.find(l => l.id === legId);
        if (main && !main.isHedge) {
          next = next.map(l =>
            (l.isHedge && l.hedgeForLegId === legId)
              ? { ...l, totalLots: main.totalLots }
              : l
          );
        }
      }
      // A hedge is only valid on a SELL main. If a hedged main is flipped off the
      // SELL side, its hedge is no longer valid — drop it (mirrors the main→hedge
      // remove cascade). The "Add hedge" affordance is also hidden on non-SELL mains.
      if (updates.actionType !== undefined) {
        const main = next.find(l => l.id === legId);
        if (main && !main.isHedge && main.actionType !== 'SELL') {
          next = next.filter(l => !(l.isHedge && l.hedgeForLegId === legId));
        }
      }
      // Recycle depends on the main having an early-exit-or-re-entry trigger. If a main
      // loses ALL of SL / TSL / TP / re-entry / re-execute, clear the recycle opt-in on its
      // hedge so we never persist a recycle hedge on an ineligible main (backend
      // validate_hedge_legs rejects it, and the builder hides the toggle once ineligible).
      if (updates.stopLoss !== undefined || updates.trailingStopLoss !== undefined ||
          updates.targetProfit !== undefined || updates.reEntry !== undefined ||
          updates.reExecute !== undefined) {
        const main = next.find(l => l.id === legId);
        if (main && !main.isHedge && !mainRecycleEligible(main)) {
          next = next.map(l =>
            (l.isHedge && l.hedgeForLegId === legId && l.hedgeRecycle)
              ? { ...l, hedgeRecycle: false }
              : l
          );
        }
      }
      return next;
    });
  }, [setLegs]);
  
  // Position actions object
  const actions: PositionActions = {
    add: addPosition,
    addHedge,
    remove: removePosition,
    copy: copyPosition,
    update: updatePosition
  };

  return {
    actions,
    updatePosition
  };
};